import { DataVersion, UnifiedMonitoringProduct } from '@hakimo-ui/hakimo/types';
import { buildUnifiedMonitoringUrl } from '@hakimo-ui/hakimo/util';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { EscalationState } from '../types';
dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

interface Props {
  siblingEscalations: EscalationState[];
  timezone: string;
  locationName: string;
  /**
   * Product and version of the group being viewed. Siblings are escalations at
   * the same location, so they share both -- used to address them in the right
   * Unified Monitoring bucket.
   */
  product?: UnifiedMonitoringProduct;
  dataVersion?: DataVersion;
}

export function SiblingEscalationBanner(props: Props) {
  const { siblingEscalations, timezone, locationName, product, dataVersion } =
    props;
  const timeFormat = 'ddd hh:mm:ss A, D-MMM-YY';

  return (
    <div className="flex rounded-md border-red-200 bg-red-50 p-2 text-sm text-red-800 dark:border-red-500/50 dark:bg-red-700/50 dark:text-red-200">
      <details className="ml-4">
        <summary>
          <span className="font-semibold">
            Other Escalations at {locationName}:{' '}
          </span>
          {siblingEscalations.length} other alarms are currently escalated.
          Expand to view details
        </summary>
        {siblingEscalations.map((escalation) => (
          <li
            key={escalation.alarmGroupId}
            className="ml-4 list-item list-inside"
          >
            <span className="inline-flex items-center gap-2">
              <span>
                Alarm escalated at{' '}
                {dayjs(escalation.escalationOpenTimestamp)
                  .tz(timezone)
                  .format(timeFormat)}
                : {escalation.comment}
              </span>
              <a
                href={buildUnifiedMonitoringUrl(escalation.alarmGroupId, {
                  dataVersion,
                  product,
                })}
                target="_blank"
                rel="noreferrer"
                title="Open Alarm group"
                className="text-sm hover:underline"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            </span>
          </li>
        ))}
      </details>
    </div>
  );
}
